/**
 * 提交历史记录项组件
 * 展示单条提交记录的状态统计和信息
 */

import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import {
  Clock,
  Loader2,
  CheckCircle,
  XCircle,
  MessageSquare,
  Eye,
  Timer,
  User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { SubmissionRecord } from '@/types/submission'
import { BATCH_TASK_MODE } from '@/types/task'

/**
 * 提交记录项组件属性
 */
interface SubmissionHistoryItemProps {
  /** 提交记录 */
  record: SubmissionRecord
}

/**
 * 格式化相对时间
 * @param dateString - ISO 8601 时间字符串
 * @returns 格式化后的相对时间
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) {
    return '刚刚'
  }
  if (diffMin < 60) {
    return `${String(diffMin)} 分钟前`
  }
  if (diffHour < 24) {
    return `${String(diffHour)} 小时前`
  }
  if (diffDay < 7) {
    return `${String(diffDay)} 天前`
  }

  return date.toLocaleDateString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 格式化计划执行时间
 * @param dateString - ISO 8601 时间字符串
 * @returns 格式化后的时间
 */
function formatScheduledTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * 状态芯片组件属性
 */
interface StatusChipProps {
  iconComponent: typeof Clock
  label: string
  count: number
  colorClass: string
  spinning?: boolean
}

/**
 * 状态芯片组件（紧凑版）
 * @param props - 组件属性
 * @returns 状态芯片 ReactElement
 */
function StatusChip({
  iconComponent,
  label,
  count,
  colorClass,
  spinning = false,
}: StatusChipProps): ReactElement {
  const IconComponent = iconComponent
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px]',
        colorClass
      )}
    >
      <IconComponent className={cn('h-3 w-3', spinning && 'animate-spin')} />
      <span className="font-medium">{count}</span>
      <span className="opacity-75">{label}</span>
    </div>
  )
}

/**
 * 迷你圆环进度组件属性
 */
interface MiniCircleProgressProps {
  /** 进度百分比 (0-100) */
  progress: number
  /** 是否运行中 */
  isRunning?: boolean
  /** 是否有失败 */
  hasFailed?: boolean
}

/**
 * 迷你圆环进度组件
 * @param props - 组件属性
 * @returns 迷你圆环进度 ReactElement
 */
function MiniCircleProgress({
  progress,
  isRunning = false,
  hasFailed = false,
}: MiniCircleProgressProps): ReactElement {
  const size = 24
  const strokeWidth = 2.5
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (progress / 100) * circumference

  // 根据状态决定颜色
  const progressColor = hasFailed
    ? 'stroke-amber-500'
    : isRunning
      ? 'stroke-blue-500'
      : 'stroke-emerald-500'

  const textColor = hasFailed
    ? 'text-amber-600 dark:text-amber-400'
    : isRunning
      ? 'text-blue-600 dark:text-blue-400'
      : 'text-emerald-600 dark:text-emerald-400'

  return (
    <div className="flex items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          className={cn('-rotate-90', isRunning && 'animate-pulse')}
          width={size}
          height={size}
        >
          {/* 背景圆环 */}
          <circle
            className="stroke-muted"
            strokeWidth={strokeWidth}
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          {/* 进度圆环 */}
          <circle
            className={cn('transition-all duration-300', progressColor)}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
            }}
          />
        </svg>
      </div>
      <span className={cn('text-xs font-medium', textColor)}>
        {Math.round(progress)}%
      </span>
    </div>
  )
}

/**
 * 提交历史记录项组件
 * @param props - 组件属性
 * @returns 提交记录项 ReactElement
 */
export function SubmissionHistoryItem({
  record,
}: SubmissionHistoryItemProps): ReactElement {
  const { stats, remark, submitted_at: submittedAt, task_mode: taskMode, scheduled_at: scheduledAt } = record

  // 计算完成进度百分比
  const progressPercent = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0

  // 判断任务状态
  const isRunning = stats.queued > 0 || stats.running > 0
  const isCompleted = stats.completed === stats.total
  const hasFailed = stats.failed > 0
  const isScheduled = taskMode === BATCH_TASK_MODE.SCHEDULED

  // 是否有提交者信息（仅主账号查看时有值）
  const hasOperatorInfo = record.operator_username !== undefined && record.operator_username !== ''

  return (
    <div
      className={cn(
        'group rounded-lg border bg-card px-4 py-3 transition-all',
        'hover:border-primary/50 hover:shadow-sm'
      )}
    >
        {/* 主要信息行：备注 + 状态统计 + 进度 + 操作 */}
        <div className="flex items-center justify-between gap-4">
          {/* 左侧：备注信息 */}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              {remark !== '' ? (
                <>
                  <MessageSquare className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">{remark}</span>
                </>
              ) : (
                <span className="text-sm text-muted-foreground">无备注</span>
              )}
              {/* 状态标签 - 紧跟在备注后面 */}
              <div className="flex shrink-0 items-center gap-1 ml-1">
                {isScheduled && (
                  <span className="inline-flex items-center gap-0.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                    <Timer className="h-2.5 w-2.5" />
                    定时
                  </span>
                )}
                {isRunning && (
                  <span className="inline-flex items-center rounded-full border border-blue-500/20 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                    处理中
                  </span>
                )}
                {isCompleted && !hasFailed && (
                  <span className="inline-flex items-center rounded-full border border-emerald-500/20 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                    已完成
                  </span>
                )}
                {isCompleted && hasFailed && (
                  <span className="inline-flex items-center rounded-full border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                    部分失败
                  </span>
                )}
              </div>
            </div>
            {/* 提交者信息 + 时间 - 放在同一行 */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {hasOperatorInfo && (
                <>
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    <span>
                      {record.is_sub_account === true ? '子账号: ' : '提交者: '}
                      {record.operator_username}
                    </span>
                  </div>
                  <span className="text-muted-foreground/50">•</span>
                </>
              )}
              <span>{formatRelativeTime(submittedAt)}</span>
              {isScheduled && scheduledAt !== undefined && (
                <>
                  <span className="text-muted-foreground/50">•</span>
                  <span className="text-blue-600 dark:text-blue-400">
                    计划: {formatScheduledTime(scheduledAt)}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* 中间：状态统计芯片 */}
          <div className="flex shrink-0 flex-wrap items-center gap-1">
            {stats.queued > 0 && (
              <StatusChip
                iconComponent={Clock}
                label="排队"
                count={stats.queued}
                colorClass="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
              />
            )}
            {stats.running > 0 && (
              <StatusChip
                iconComponent={Loader2}
                label="生成中"
                count={stats.running}
                colorClass="bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                spinning
              />
            )}
            {stats.failed > 0 && (
              <StatusChip
                iconComponent={XCircle}
                label="失败"
                count={stats.failed}
                colorClass="bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
              />
            )}
            {stats.cancelled !== undefined && stats.cancelled > 0 && (
              <StatusChip
                iconComponent={XCircle}
                label="已取消"
                count={stats.cancelled}
                colorClass="bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400"
              />
            )}
            {stats.completed > 0 && (
              <StatusChip
                iconComponent={CheckCircle}
                label="完成"
                count={stats.completed}
                colorClass="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
              />
            )}
          </div>

          {/* 右侧：进度 + 操作按钮 */}
          <div className="flex shrink-0 items-center gap-2">
            {/* 迷你圆环进度 */}
            <MiniCircleProgress
              progress={progressPercent}
              isRunning={isRunning}
              hasFailed={hasFailed}
            />
            {/* 查看详情按钮 */}
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
            >
              <Link to={`/records/generations/${record.id}`}>
                <Eye className="mr-1 h-3.5 w-3.5" />
                详情
              </Link>
            </Button>
          </div>
        </div>
      </div>
  )
}
